import { liveWindowAt } from '@apm/contracts';

import { channelBadgeKey } from '../../channel-badge.js';
import type { EpgRow } from '../../epg.js';
import type { ItemBrief } from '../../home/compose-home.js';
import { useT } from '../../i18n/LocaleProvider.js';
import { itemText, revealOnFocus } from './labels.js';

/**
 * 채널 카드 — 「즐겨찾기」·「채널」 레일의 한 장. **네이티브 `<button>`** (절대 규칙 29).
 *
 * 앞에 **채널 번호**를 쓴다(절대 규칙 71 — 숫자 키가 그 번호를 부른다). 종류 배지는
 * 편성표의 키 그대로이고 ONAIR 는 `TV` 다(Q4 — 그 행에만 · 생방송이 아니라 서버가 잇는 채널이다).
 */
export function ChannelCard({
  row,
  now,
  onPick,
}: {
  row: EpgRow;
  now: ItemBrief | null;
  onPick: (channelId: string) => void;
}) {
  const t = useT();
  const { channel } = row;
  const onair = channel.delivery === 'onair';
  const badgeKey = channelBadgeKey(channel);
  /*
   * v33. **지금 라이브가 도는 채널인가** (`docs/67` §8.3). 배지 하나가 전부이고,
   * 프리뷰는 포스터다(`preview-source.ts` 의 ★ — 첫 녹화물을 틀면 거짓말이다).
   */
  const live = liveWindowAt(row.liveEvents, Date.now()).current !== null;
  return (
    <button
      type="button"
      className="home__card home__card--channel"
      data-testid="home-card-channel"
      data-channel-id={channel.channelId}
      data-channel-number={channel.number}
      data-channel-kind={channel.kind}
      data-channel-delivery={channel.delivery}
      onFocus={revealOnFocus}
      onClick={() => {
        onPick(channel.channelId);
      }}
    >
      {channel.thumbnailUrl !== null && (
        <img className="home__card-art" src={channel.thumbnailUrl} alt="" loading="lazy" />
      )}
      <span className="home__card-body">
        {/* 번호 · 이름 · 종류 배지가 한 줄이다 — masthead 의 채널 줄과 같은 순서(2026-09-10 실기) */}
        <span className="home__card-title">
          <span className="home__number">{channel.number}</span>
          <span className="home__card-name" data-i18n="data">
            {channel.name}
          </span>
          {live ? (
            <span className="home__badge home__badge--live" data-testid="home-live-badge">
              {t('epg.liveBadge')}
            </span>
          ) : onair ? (
            <span className="home__badge home__badge--onair">{t('onair.badge')}</span>
          ) : (
            // v33-g. 일반 채널은 **장르**가 배지다 — 없으면 그리지 않는다(`channel-badge.ts`)
            badgeKey !== null && <span className="home__badge">{t(badgeKey)}</span>
          )}
        </span>
        {now !== null && (
          <span className="home__card-meta">
            <span className="home__card-now" data-i18n="data">
              {itemText(t, now)}
            </span>
          </span>
        )}
      </span>
    </button>
  );
}
